import { captureException } from '@sentry/react'
import { useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import type { ReactElement, ReactNode } from 'react'
import type { Accept, DropzoneRef, FileWithPath } from 'react-dropzone'

import Wrapper from '../Wrapper'

import Button from '~/components/Buttons/Button'
import Dropzone from '~/components/Dropzone'
import FilePreview from '~/components/FilePreview'
import Label from '~/components/Label'
import { useCreateFileMutation, useFilesDetailsQuery, useUploadFileMutation } from '~/queries/files'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface Props {
  accept?: Accept
  className?: string
  error?: ReactNode
  label?: string
  multiple?: boolean
  name: string
  required?: boolean
  value?: string[] | null
}

const FileUploadInput = ({ accept, className, error, label, multiple = true, name, required }: Props): ReactElement => {
  /** Local state */

  const { formState, setValue, watch } = useFormContext()

  const { isSubmitting } = formState

  const dropzoneRef = useRef<DropzoneRef>(null)

  const watchedFiles: string[] = watch(name)

  const [isUploading, setIsUploading] = useState(false)

  /** Queries & Mutations */

  const { data: filesData } = useFilesDetailsQuery(watchedFiles || [])

  const { mutateAsync: createFile } = useCreateFileMutation()

  const { mutateAsync: uploadFile } = useUploadFileMutation()

  /** Handlers */

  const handleAddFile = () => dropzoneRef.current && dropzoneRef.current.open()

  const handleDrop = async (files: FileWithPath[]) => {
    const filesToSetToFormValue: string[] = new Array(files.length)
    setIsUploading(true)

    try {
      await Promise.all(
        files.map(async (file, index) => {
          const tempFile = await createFile({
            mimeType: file.type,
            name: file.name,
            size: file.size,
          })
          uploadFile({ file, mimeType: file.type, uploadUrl: tempFile.upload_url })
          filesToSetToFormValue[index] = tempFile.id
        })
      )

      setValue(name, watchedFiles && multiple ? [...watchedFiles, ...filesToSetToFormValue] : filesToSetToFormValue, {
        shouldDirty: true,
        shouldTouch: true,
      })
    } catch (e) {
      captureException(e)
      displayToast('An error has occured', 'error')
    } finally {
      setIsUploading(false)
    }
  }

  const handleDeleteFile = (fileId: string) => () =>
    setValue(
      name,
      watchedFiles.filter((el) => el !== fileId),
      {
        shouldDirty: true,
        shouldTouch: true,
      }
    )

  return (
    <Wrapper className={className} error={error}>
      <div className="file-upload-input-component">
        <div className="file-upload-input-component__dropzone-wrapper">
          {label ? (
            <Label className="file-upload-input-component__dropzone-wrapper__label" label={label} required={required} />
          ) : null}
          <Dropzone
            accept={accept}
            className="file-upload-input-component__dropzone-wrapper__hidden-dropzone"
            dropzoneRef={dropzoneRef}
            isLoading={isUploading}
            multiple={multiple}
            onDrop={handleDrop}
          />
          <Button
            className="file-upload-input-component__dropzone-wrapper__add-button"
            disabled={isUploading || isSubmitting}
            onClick={handleAddFile}
            type="button"
          >
            {isUploading ? <div className="lds-dual-ring" /> : `Upload File${multiple ? 's' : ''}`}
          </Button>
        </div>
        {filesData?.length ? (
          <div className="file-upload-input-component__previews">
            {filesData.map(({ download_url, id, mime_type, name }) => (
              <FilePreview
                className="small"
                isEditable={true}
                key={id}
                mimeType={mime_type}
                name={name}
                onRemove={handleDeleteFile(id)}
                src={download_url}
                useVideoPlayer
                withQualityIdentifier={false}
              />
            ))}
          </div>
        ) : null}
      </div>
    </Wrapper>
  )
}

export default FileUploadInput
